import React, { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface InputTextAreaProps<FieldsType extends FieldValues> {
  placeholder?: string;
  label: string;
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  required?: boolean;
  requiredMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLTextAreaElement>;
}

function InputTextArea<FieldsType extends FieldValues>({
  placeholder,
  label,
  name,
  control,
  required,
  requiredMessage,
  rules,
  inputProps,
}: InputTextAreaProps<FieldsType>) {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      rules={{
        required: requiredMessage || (required && "Required"),
        validate: (value) =>
          value.trim().length > 0 || "Please provide valid input",
        ...(rules || {}),
      }}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="relative">
          <label className="text-base ml-1 mb-0.5 block" htmlFor={field?.name}>
            <span>
              {t(label)}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </span>
          </label>
          <textarea
            id={field.name}
            {...field}
            {...(inputProps || {})}
            placeholder={t(placeholder || label)}
            className="placeholder:text-gray-500 border-gray-200 rounded-lg w-full px-2.5 py-3 border focus:border-brand-hover
            focus-within:border-brand-hover focus:bg-[#eaf1fa]  transition-all focus:ring-0 focus-visible:ring-0 focus-visible:border-brand-hover  outline-none resize-none block"
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {t(error?.message)}
          </p>
        </div>
      )}
    />
  );
}

export default InputTextArea;
